import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  CardContent,
  Badge,
  Spinner,
  Alert,
  Checkbox,
  ConfirmationDialog,
} from '@/components/ui';
import { ScoreDisplay } from '@/components/evaluation';
import {
  useEvaluations,
  useDeleteEvaluation,
  useBulkDeleteEvaluations,
  useDeleteAllEvaluations,
} from '@/hooks';
import { usePendingJobs } from '@/hooks/usePendingJobs';
import { formatDate } from '@/utils/formatters';
import { ROLLING_WINDOW_DAYS } from '@/utils/constants';
import type { EvaluationJob } from '@/services/evaluationApi';

// ─── Pending job status badge ─────────────────────────────────────────────────

const STATUS_BADGE: Record<EvaluationJob['status'], { label: string; variant: 'info' | 'warning' | 'success' | 'error' }> = {
  queued: { label: 'Queued', variant: 'warning' },
  processing: { label: 'Processing', variant: 'info' },
  // PS-0a: scored = score ready, coaching still generating
  scored: { label: 'Score ready', variant: 'info' },
  completed: { label: 'Complete', variant: 'success' },
  failed: { label: 'Failed', variant: 'error' },
};

const STAGE_SHORT: Record<string, string> = {
  pose_extraction: 'Extracting pose',
  movement_preparation: 'Preparing comparison',
  // Deprecated alias for in-flight jobs queued before the 2026-10-07 rename of
  // "3d_lifting" (nothing builds a 3D model there any more) — same label.
  // See EvaluationProgressCard.tsx for when it may be dropped.
  '3d_lifting': 'Preparing comparison',
  scoring: 'Scoring',
  coaching: 'Generating feedback',
  saving: 'Saving',
  completed: 'Done',
};

function EvaluationsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Feature 4 — "Show only evals I submitted" — persisted in URL query string
  const submittedByMe = searchParams.get('submitted_by_me') === 'true';
  function handleSubmittedByMeChange(checked: boolean) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) {
          next.set('submitted_by_me', 'true');
        } else {
          next.delete('submitted_by_me');
        }
        return next;
      },
      { replace: true }
    );
  }

  const { data: allEvaluations, isLoading } = useEvaluations();
  // F4 — pending jobs section
  const { data: pendingJobs = [] } = usePendingJobs();
  const { mutate: deleteEvaluation } = useDeleteEvaluation();
  const bulkDelete = useBulkDeleteEvaluations();
  const deleteAll = useDeleteAllEvaluations();

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal state
  const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteResult, setDeleteResult] = useState<{
    type: 'success' | 'error' | 'partial';
    message: string;
  } | null>(null);

  // Rolling-window filter: only show evals from the last ROLLING_WINDOW_DAYS days.
  // The full archive is accessible via the athlete dashboard.
  const evaluations = useMemo(() => {
    if (!allEvaluations) return undefined;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - ROLLING_WINDOW_DAYS);
    return allEvaluations.filter((e) => {
      if (!e.created_at) return false;
      if (new Date(e.created_at) < cutoff) return false;
      // Feature 4 — filter to evals submitted by the current user
      if (submittedByMe && user?.id && e.created_by_user_id !== user.id) return false;
      return true;
    });
  }, [allEvaluations, submittedByMe, user?.id]);

  // Computed selection state
  const evaluationIds = useMemo(
    () => evaluations?.map((e) => e.evaluation_id) ?? [],
    [evaluations]
  );

  const allSelected = evaluationIds.length > 0 && selectedIds.size === evaluationIds.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < evaluationIds.length;
  const noneSelected = selectedIds.size === 0;

  // Selection handlers
  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(evaluationIds));
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Delete handlers
  const handleDeleteSingle = (evaluationId: string) => {
    if (confirm('Are you sure you want to delete this evaluation?')) {
      deleteEvaluation(evaluationId);
    }
  };

  const handleDeleteSelected = () => {
    bulkDelete.mutate(Array.from(selectedIds), {
      onSuccess: (result) => {
        setSelectedIds(new Set());
        setShowDeleteSelectedModal(false);

        if (result.failed_count === 0) {
          setDeleteResult({
            type: 'success',
            message: `Successfully deleted ${result.deleted_count} evaluation(s).`,
          });
        } else if (result.deleted_count === 0) {
          setDeleteResult({
            type: 'error',
            message: `Failed to delete all ${result.failed_count} evaluation(s).`,
          });
        } else {
          setDeleteResult({
            type: 'partial',
            message: `Deleted ${result.deleted_count} evaluation(s). ${result.failed_count} failed.`,
          });
        }
      },
      onError: (error) => {
        setShowDeleteSelectedModal(false);
        setDeleteResult({
          type: 'error',
          message: `Delete failed: ${error.message}`,
        });
      },
    });
  };

  const handleDeleteAll = () => {
    deleteAll.mutate(undefined, {
      onSuccess: (result) => {
        setSelectedIds(new Set());
        setShowDeleteAllModal(false);
        setDeleteResult({
          type: 'success',
          message: result.message,
        });
      },
      onError: (error) => {
        setShowDeleteAllModal(false);
        setDeleteResult({
          type: 'error',
          message: `Delete all failed: ${error.message}`,
        });
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Evaluations"
        description="View and manage athlete evaluations"
        action={
          <Button onClick={() => navigate('/templates')}>
            <PlusIcon className="h-5 w-5 mr-2" />
            New Evaluation
          </Button>
        }
      />

      {/* Result alert */}
      {deleteResult && (
        <Alert
          variant={
            deleteResult.type === 'success'
              ? 'success'
              : deleteResult.type === 'error'
              ? 'error'
              : 'warning'
          }
          onClose={() => setDeleteResult(null)}
          className="mb-4"
        >
          {deleteResult.message}
        </Alert>
      )}

      {/* F4 — Pending Evaluations section (shown above completed list) */}
      {pendingJobs.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3">
            In Progress ({pendingJobs.length})
          </h2>
          <div className="space-y-3">
            {pendingJobs.map((job) => {
              const badge = STATUS_BADGE[job.status] ?? STATUS_BADGE.processing;
              const stageLabel = job.progress_stage
                ? (STAGE_SHORT[job.progress_stage] ?? job.progress_stage)
                : null;
              const pct = job.progress_pct ?? (job.status === 'processing' ? 10 : 0);
              return (
                <Card
                  key={job.job_id}
                  className="border border-border-subtle hover:border-border-strong transition-colors"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Status spinner / indicator */}
                      <div className="flex-shrink-0">
                        {job.status === 'processing' ? (
                          <Spinner size="sm" />
                        ) : (
                          <div className="h-5 w-5 rounded-full bg-warning/20 flex items-center justify-center">
                            <span className="text-warning text-xs font-bold">Q</span>
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-text-primary">
                            Evaluation in progress
                          </span>
                          <Badge variant={badge.variant} size="sm">
                            {badge.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-text-muted mb-2">
                          Submitted {formatDate(job.submitted_at)}
                        </p>
                        {stageLabel && (
                          <p className="text-xs text-text-secondary mb-2">{stageLabel}...</p>
                        )}
                        {job.status === 'processing' && (
                          <div className="h-1.5 bg-dark-elevated-2 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-accent rounded-full transition-all duration-500"
                              style={{ width: `${Math.max(pct, 5)}%` }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Link to the polling page — navigate to the template eval page
                          if we know the template. Since JobStatusResponse doesn't expose
                          template_id, we can't build the exact URL without a backend change.
                          Link to /evaluations as a fallback. */}
                      <div className="flex-shrink-0">
                        <Link
                          to="/evaluations"
                          className="text-xs text-accent underline"
                          aria-label="View evaluation status"
                        >
                          View
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" label="Loading evaluations..." />
        </div>
      ) : evaluations && evaluations.length > 0 ? (
        <>
          {/* Feature 4 — "Show only evals I submitted" filter */}
          <div className="flex items-center gap-2 mb-3">
            <Checkbox
              checked={submittedByMe}
              onChange={(e) => handleSubmittedByMeChange((e.target as HTMLInputElement).checked)}
              label="Show only evals I submitted"
            />
          </div>

          {/* Bulk actions toolbar */}
          <div className="flex items-center justify-between bg-dark-elevated-1 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-4">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
                label={allSelected ? 'Deselect all' : 'Select all'}
              />
              {selectedIds.size > 0 && (
                <span className="text-sm text-text-secondary">
                  {selectedIds.size} selected
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteSelectedModal(true)}
                disabled={noneSelected}
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete Selected
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteAllModal(true)}
                className="text-danger border-danger/40 hover:bg-danger/10"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete All
              </Button>
            </div>
          </div>

          {/* Evaluation cards */}
          <div className="space-y-4">
            {evaluations.map((evaluation) => (
              <Card
                key={evaluation.evaluation_id}
                className="hover:border-border-strong transition-colors"
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-6">
                    {/* Checkbox */}
                    <div
                      className="flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selectedIds.has(evaluation.evaluation_id)}
                        onChange={() => handleSelectOne(evaluation.evaluation_id)}
                      />
                    </div>

                    {/* Score display */}
                    <div
                      className="flex-shrink-0 cursor-pointer"
                      onClick={() => navigate(`/evaluations/${evaluation.evaluation_id}`)}
                    >
                      <ScoreDisplay score={evaluation.overall_score ?? 0} size="sm" label="" />
                    </div>

                    {/* Details */}
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => navigate(`/evaluations/${evaluation.evaluation_id}`)}
                    >
                      <h3
                        className="text-base text-text-primary mb-1"
                        style={{ fontFamily: 'var(--font-editorial)', fontWeight: 500 }}
                      >
                        Evaluation {evaluation.evaluation_id.slice(0, 8)}
                      </h3>
                      <p className="text-sm text-text-secondary mb-2">
                        {formatDate(evaluation.created_at)}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="secondary" size="sm">
                          {evaluation.phase_scores?.length ?? 0} phases
                        </Badge>
                        <Badge variant="secondary" size="sm">
                          {evaluation.region_scores?.length ?? 0} regions
                        </Badge>
                        {evaluation.metadata && (
                          <Badge variant="secondary" size="sm">
                            {evaluation.metadata.total_frames} frames
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSingle(evaluation.evaluation_id);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Rolling-window footnote */}
          <p
            className="mt-6 text-center text-xs"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Showing evaluations from the last {ROLLING_WINDOW_DAYS} days. Older evals are summarized in your{' '}
            <Link
              to="/"
              style={{ color: 'var(--color-accent-blue)', textDecoration: 'underline' }}
            >
              athlete dashboard
            </Link>
            .
          </p>
        </>
      ) : evaluations !== undefined && evaluations.length === 0 && (allEvaluations?.length ?? 0) > 0 ? (
        // Filtered list is empty but there are evals in the full set
        <div className="text-center py-12">
          {submittedByMe ? (
            <>
              {/* Feature 4 — filter-active empty state */}
              <div className="flex items-center gap-2 justify-center mb-3">
                <Checkbox
                  checked={submittedByMe}
                  onChange={(e) => handleSubmittedByMeChange((e.target as HTMLInputElement).checked)}
                  label="Show only evals I submitted"
                />
              </div>
              <p className="text-text-secondary mb-2">
                No evaluations you submitted in the last {ROLLING_WINDOW_DAYS} days.
              </p>
              <button
                className="text-sm font-medium"
                style={{ color: 'var(--color-accent-blue)', background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() => handleSubmittedByMeChange(false)}
              >
                Show all evals &rarr;
              </button>
            </>
          ) : (
            <>
              <p className="text-text-secondary mb-2">
                No evaluations in the last {ROLLING_WINDOW_DAYS} days.
              </p>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                You have older evaluations archived on your dashboard.
              </p>
              <Link to="/" className="text-sm font-medium" style={{ color: 'var(--color-accent-blue)' }}>
                View older evaluations in your dashboard &rarr;
              </Link>
            </>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-text-secondary mb-4">No evaluations yet</p>
          <Button onClick={() => navigate('/templates')}>
            Create your first evaluation
          </Button>
        </div>
      )}

      {/* Delete Selected Confirmation Modal */}
      <ConfirmationDialog
        isOpen={showDeleteSelectedModal}
        onClose={() => setShowDeleteSelectedModal(false)}
        onConfirm={handleDeleteSelected}
        title="Delete Selected Evaluations"
        description={`Are you sure you want to delete ${selectedIds.size} evaluation(s)? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="warning"
        isLoading={bulkDelete.isPending}
      />

      {/* Delete All Confirmation Modal */}
      <ConfirmationDialog
        isOpen={showDeleteAllModal}
        onClose={() => setShowDeleteAllModal(false)}
        onConfirm={handleDeleteAll}
        title="Delete All Evaluations"
        description={`This will permanently delete ALL ${allEvaluations?.length ?? 0} evaluations. This action cannot be undone.`}
        confirmLabel="Delete All"
        variant="danger"
        isLoading={deleteAll.isPending}
      />
    </div>
  );
}

export default EvaluationsList;
